import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { Container, Row, Column, Carousel } from '@pella/sash-framework';
import { Hero, Layout, SEO, CTA, Image } from '../../components';

import AnchorNav from '../../components/common/anchor-nav';
import Cards from '../../components/common/cards';
import Video from '../../components/common/video';
import CTARibbon from '../../components/common/cta-ribbon';
import FNOTabs from '../../components/template/product/fno-tabs';
import DownloadTabs from '../../components/common/download-tabs';

const Product = (props) => {
	const pageData = props.data.contentstackPalProduct;

	return (
		<Layout>
			{pageData.global_hero?.heading && (
				<Hero
					data={pageData.global_hero}
					className='mb-10'
				/>
			)}

			{pageData.anchor_links?.[0] && <AnchorNav data={pageData.anchor_links} />}

			{pageData.modular_blocks?.[0] &&
				pageData.modular_blocks.map((block, index) => {
					if (block.carousel != null) {
						return (
							<div
								id={block.carousel.anchor_text}
								key={index}
								className='palProduct-carousel my-12 xl:my-16'
							>
								<Container>
									<Carousel showDots>
										{block.carousel.group.map((item, index) => (
											<Row
												key={index}
												verticalGutters
											>
												<Column
													xs={12}
													md={6}
												>
													{item.image?.[0]?.file?.url && (
														<Image
															data={item.image[0]}
															className='aspect-[1.3] w-full'
														/>
													)}

													{item.image?.[0]?.video_id && <Video data={item.image[0]} />}
												</Column>

												<Column
													xs={12}
													md={6}
												>
													<div className='xl:pl-5'>
														{item.heading && (
															<h2
																className='mb-4 xl:mb-6'
																dangerouslySetInnerHTML={{ __html: item.heading }}
															/>
														)}
														{item.subheading && (
															<h3
																className='mb-4 font-normal !leading-7'
																dangerouslySetInnerHTML={{ __html: item.subheading }}
															/>
														)}
														{item.description && (
															<div
																className='text-sm md:text-base xl:text-lg'
																dangerouslySetInnerHTML={{ __html: item.description }}
															/>
														)}
													</div>
												</Column>
											</Row>
										))}
									</Carousel>
								</Container>
							</div>
						);
					}

					if (block.designer != null) {
						return (
							<div
								key={index}
								id={block.designer.anchor_text}
								className='my-12 text-center xl:my-16'
							>
								<Container>
									{block.designer.heading && <h2 dangerouslySetInnerHTML={{ __html: block.designer.heading }} />}
									{block.designer.description && (
										<div
											className='my-6 xl:mb-10'
											dangerouslySetInnerHTML={{ __html: block.designer.description }}
										/>
									)}

									{block.designer.cob?.[0] && (
										<Row
											className='md:justify-center'
											verticalGutters
										>
											{block.designer.cob.map((cob, index) => (
												<Column
													xs={6}
													sm={4}
													md={3}
													key={index}
													className={`xl:max-w-[20%] xl:flex-[0_0_20%] ${index > 0 ? 'border-l border-l-gray-15' : ''}`}
												>
													<CTA
														data={cob}
														className='block font-semibold no-underline hover:underline [&>div]:mx-auto [&>div]:mb-4 [&>div]:!block [&_img]:!h-20 [&_img]:!object-contain md:[&_img]:!h-24 xl:[&_img]:!h-28'
													/>
												</Column>
											))}
										</Row>
									)}
								</Container>
							</div>
						);
					}

					if (block.cards != null) {
						return (
							<Cards
								key={index}
								data={block.cards}
							/>
						);
					}

					if (block.cta_ribbon?.cta_ribbon?.[0]) {
						return (
							<Container key={index}>
								<CTARibbon data={block.cta_ribbon.cta_ribbon[0]} />
							</Container>
						);
					}

					if (block.features != null) {
						return (
							<FNOTabs
								data={block.features}
								key={index}
							/>
						);
					}

					if (block.downloads != null) {
						return (
							<DownloadTabs
								key={index}
								data={block.downloads}
							/>
						);
					}
				})}
		</Layout>
	);
};

Product.propTypes = {
	data: PropTypes.object,
};

export default Product;

// eslint-disable-next-line react/prop-types -- gatsby component
export const Head = ({ data }) => <SEO {...data.contentstackPalProduct.seo} />;

export const query = graphql`
	query ($uid: String!) {
		contentstackPalProduct(uid: { eq: $uid }) {
			url
			global_hero {
				...Hero
			}
			anchor_links {
				...CTA
			}
			modular_blocks {
				carousel {
					anchor_text
					group {
						image {
							... on Contentstack_image {
								...Image
							}
							... on Contentstack_video {
								...Video
							}
						}
						heading
						subheading
						description
					}
				}
				designer {
					anchor_text
					heading
					description
					cob {
						...CTA
					}
				}
				cta_ribbon {
					cta_ribbon {
						...CTARibbon
					}
				}
				cards {
					...Cards
				}
				features {
					anchor_text
					heading
					options_tabs {
						tab_name
						internal_fno {
							...FNOOption
						}
						interstack {
							custom
						}
					}
				}
				downloads {
					anchor_text
					group {
						heading
						cta {
							...CTA
						}
					}
				}
			}
			seo {
				...SEO
			}
		}
	}
`;
